import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue,get } from "firebase/database";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import { Eye, Download, ArrowLeft, Info, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./UserProofOfDelivery.css";

const UserProofOfDelivery = () => {
  const { userId, loading: userLoading } = useGetCurrentUserId();
  const [documents, setDocuments] = useState([]);
  const [fullOrders, setFullOrders] = useState({});
  const [transporterProfiles, setTransporterProfiles] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
  if (userLoading || !userId) {
    setIsLoading(false);
    return;
  }

  const loadData = async () => {
    setIsLoading(true);

    const db = getDatabase();

    const docsRef = ref(db, "transport_documents");
    const ordersRef = ref(db, "transport_orders");
    const profilesRef = ref(db, "transporter-profile");

    try {
      // Load transporter profiles once
      const profileSnap = await get(profilesRef);
      const profiles = profileSnap.val() || {};

      setTransporterProfiles(profiles);

      // Listen for Documents
      const unsubDocs = onValue(docsRef, (snapshot) => {
        if (!snapshot.exists()) {
          setDocuments([]);
          setIsLoading(false);
          return;
        }

        const allTransportersData = snapshot.val();
        const allDocs = [];

        Object.keys(allTransportersData).forEach((transporterId) => {
          const transporterDocs = allTransportersData[transporterId];
          const profile = profiles[transporterId] || {};

          // POD
          if (transporterDocs?.POD) {
            Object.keys(transporterDocs.POD).forEach((podKey) => {
              const pod = transporterDocs.POD[podKey];

              if (pod.clientId === userId) {
                allDocs.push({
                  id: podKey,
                  type: "POD",
                  transporterId,
                  transporterName:
                    profile.company ||
                    profile.contactPerson ||
                    profile.name ||
                    "Unknown Transporter",
                  transporterPhone: profile.phone || "N/A",
                  ...pod,
                  invoices: [],
                });
              }
            });
          }

          // Invoices
          if (transporterDocs?.invoices) {
            Object.keys(transporterDocs.invoices).forEach((invKey) => {
              const invoice = transporterDocs.invoices[invKey];

              if (invoice.orderId) {
                const podIndex = allDocs.findIndex(
                  (d) => d.orderId === invoice.orderId
                );

                if (podIndex !== -1) {
                  allDocs[podIndex].invoices.push({
                    ...invoice,
                    id: invKey,
                  });
                }
              }
            });
          }
        });

        allDocs.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));

        setDocuments(allDocs);
        setIsLoading(false);
      });

      // Listen for Orders
      const unsubOrders = onValue(ordersRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const orderMap = {};

        Object.values(snapshot.val()).forEach((transporterOrders) => {
          Object.values(transporterOrders || {}).forEach((order) => {
            if (order.clientId === userId && order.orderId) {
              orderMap[order.orderId] = order;
            }
          });
        });

        setFullOrders(orderMap);
      });

      return () => {
        unsubDocs();
        unsubOrders();
      };
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const cleanup = loadData();

  return () => {
    cleanup.then((fn) => fn && fn());
  };
}, [userId, userLoading]);

  const handleDownload = (url) => window.open(url, "_blank");

  const openOrderDetail = (pod) => {
    const fullOrder = fullOrders[pod.orderId];
    setSelectedOrderDetail(fullOrder || pod);
    setShowDetailPopup(true);
  };

  if (userLoading || isLoading) {
    return <div className="user-pod-loading">Loading documents...</div>;
  }

  return (
    <div className="user-pod-container">
      <div className="user-pod-header">
        <button className="user-pod-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} /> Back
        </button>
        <h1 className="user-pod-title">Documents</h1>
        <button onClick={() => setShowInfo(true)} className="user-pod-info-btn">
          <Info size={24} />
        </button>
      </div>

      {showInfo && (
        <div className="user-pod-overlay">
          <div className="user-pod-modal">
            <button className="user-pod-close" onClick={() => setShowInfo(false)}>
              <X size={24} />
            </button>
            <h4 className="mb-3 text-center">Proof of Delivery</h4>
            <p>All uploaded Proof of Delivery (POD) documents and invoices are shown here.</p>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <p className="user-pod-no-data">No Proof of Delivery uploaded yet</p>
      ) : (
        <div className="user-pod-grid">
          {documents.filter(doc => doc.type === "POD").map((pod) => {
            const podInvoices = pod.invoices || [];

            return (
              <div key={pod.id} className="user-pod-card">
                <div className="user-pod-card-header">
                  <div>
                    <p className="user-pod-order-id">Order #{pod.orderId}</p>
                    <p className="user-pod-route">{pod.route}</p>
                  </div>
                  <button
                    onClick={() => openOrderDetail(pod)}
                    className="user-pod-view-more"
                  >
                    View More <Eye size={18} />
                  </button>
                </div>

                <div className="user-pod-info-grid">
                  <div><span>Transporter:</span> <strong>{pod.transporterName}</strong></div>
                  <div><span>Transport Phone:</span> <strong>{pod.transporterPhone}</strong></div>
                  <div><span>Truck:</span> <strong>{pod.truckNumber}</strong></div>
                  <div><span>Driver:</span> <strong>{pod.driverName || "N/A"}</strong></div>
                  <div><span>Driver Phone:</span> <strong>{pod.driverNumber || "N/A"}</strong></div>
                </div>

                {/* POD Section */}
                <div className="user-pod-section">
                  <p className="user-pod-section-title">Proof of Delivery</p>
                  <div className="user-pod-file-row">
                    <div className="user-pod-file-info">
                      <p className="user-pod-filename">{pod.fileName}</p>
                      <p className="user-pod-date">{new Date(pod.uploadedAt).toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => handleDownload(pod.fileUrl)} className="user-pod-download-btn">
                      <Download size={18} /> View
                    </button>
                  </div>
                </div>

                {/* Invoices Section */}
                <div className="user-pod-section">
                  <p className="user-pod-section-title">Invoices</p>
                  {podInvoices.length > 0 ? (
                    <div className="user-pod-invoices-list">
                      {podInvoices.map((inv, idx) => (
                        <div key={idx} className="user-pod-invoice-row">
                          <div className="user-pod-file-info">
                            <p className="user-pod-filename">{inv.fileName}</p>
                            <p className="user-pod-date">{new Date(inv.uploadedAt).toLocaleDateString()}</p>
                          </div>
                          <button onClick={() => handleDownload(inv.fileUrl)} className="user-pod-download-btn">
                            <Download size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="user-pod-no-invoice">No invoices uploaded yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Popup */}
      {showDetailPopup && selectedOrderDetail && (
        <div className="user-pod-detail-overlay">
          <div className="user-pod-detail-modal">
            <div className="user-pod-detail-header">
              <h3 className="user-pod-detail-title">Order Details</h3>
              <button 
                onClick={() => setShowDetailPopup(false)}
                className="user-pod-detail-close"
              >
                <X size={28} />
              </button>
            </div>

            <div className="user-pod-detail-content">
              <div className="user-pod-detail-grid">
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Order ID</span>
                  <strong>{selectedOrderDetail.orderId}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Status</span>
                  <strong>{selectedOrderDetail.status || "N/A"}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Total Quantity</span>
                  <strong>{selectedOrderDetail.quantity} {selectedOrderDetail.quantityUnit}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Remaining</span>
                  <strong>
                    {(selectedOrderDetail.quantity || 0) - (selectedOrderDetail.dispatchSummary?.totalLoad || 0)} {selectedOrderDetail.quantityUnit}
                  </strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">From</span>
                  <strong>{selectedOrderDetail.from}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">To</span>
                  <strong>{selectedOrderDetail.to}</strong>
                </div>
              </div>
            </div>

            <button 
              onClick={() => setShowDetailPopup(false)} 
              className="user-pod-detail-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProofOfDelivery;